import {
  ChangeDetectionStrategy,
  Component,
  output,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { DIETARY_TAGS, UK_ALLERGENS, dietaryIcon } from '../../../../core/constants/dietary';
import { AllergenChipComponent } from '../../../../shared/components/allergen-chip/allergen-chip.component';
import { PaperButtonComponent, PaperIconComponent } from '@ricoh-paper/angular';

export interface DietaryPreferencesSaved {
  dietaryTags: string[];
  allergens: string[];
}

@Component({
  selector: 'ct-dietary-preferences-setup',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AllergenChipComponent, PaperIconComponent, PaperButtonComponent, TranslocoPipe],
  template: `
    <div class="dpsd__backdrop" (click)="onCancel()"></div>
    <div class="dpsd">
      <div class="dpsd__header">
        <div>
          <h2 class="dpsd__title">{{ 'catering.customer.dietary_setup.title' | transloco }}</h2>
          <p class="dpsd__subtitle">
            {{ 'catering.customer.dietary_setup.subtitle' | transloco }}
          </p>
        </div>
        <paper-button
          class="dpsd__close"
          intent="ghost"
          size="sm"
          shape="icon"
          icon="close"
          [ariaLabel]="'catering.customer.dietary_setup.close' | transloco"
          (click)="onCancel()"
        />
      </div>

      <div class="dpsd__body">
        <div class="dpsd__section">
          <p class="dpsd__section-label">{{ 'catering.customer.dietary_setup.diets_label' | transloco }}</p>
          <div class="dpsd__diets">
            @for (tag of dietaryTags; track tag.value) {
              <paper-button
                class="dpsd__diet"
                intent="secondary"
                size="md"
                [class.selected]="selectedDietaryTags().includes(tag.value)"
                [ariaPressed]="selectedDietaryTags().includes(tag.value)"
                [style.--tag-color]="tag.colour"
                (click)="toggleDietaryTag(tag.value)">
                <span class="dpsd__diet-icon">
                  <paper-icon [name]="iconFor(tag.value)" [size]="20" aria-hidden="true" />
                </span>
                <span class="dpsd__diet-name">{{ 'catering.customer.dietary.' + tag.value | transloco }}</span>
              </paper-button>
            }
          </div>
        </div>

        <div class="dpsd__section">
          <p class="dpsd__section-label">{{ 'catering.customer.dietary_setup.allergens_label' | transloco }}</p>
          <div class="dpsd__allergens">
            @for (allergen of allergens; track allergen) {
              <im-allergen-chip
                [allergen]="allergen"
                [selected]="selectedAllergens().includes(allergen)"
                (toggle)="toggleAllergen(allergen)" />
            }
          </div>
        </div>
      </div>

      <div class="dpsd__footer">
        <paper-button intent="secondary" size="md" (click)="onSkip()">
          {{ 'catering.customer.dietary_setup.skip' | transloco }}
        </paper-button>
        <paper-button intent="primary" size="md" (click)="onSave()">
          {{ 'catering.customer.dietary_setup.save' | transloco }}
        </paper-button>
      </div>
    </div>
  `,
  styles: `
    .dpsd__backdrop {
      position: fixed;
      inset: 0;
      z-index: 9998;
      background: var(--scrim);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      animation: dpsd-fade-in 0.2s ease-out;
    }

    .dpsd {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 9999;
      width: min(460px, calc(100vw - 32px));
      max-height: calc(100vh - 64px);
      background: rgba(14, 14, 14, 0.92);
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
      border: 1px solid var(--border-low);
      border-radius: 16px;
      box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
      display: flex;
      flex-direction: column;
      animation: dpsd-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .dpsd__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      padding: 20px 20px 14px;
      border-bottom: 1px solid var(--border-low);
    }

    .dpsd__title {
      font-size: 17px;
      font-weight: 600;
      color: var(--text);
      margin: 0 0 4px;
    }

    .dpsd__subtitle {
      font-size: 13px;
      color: var(--text-secondary);
      margin: 0;
    }

    .dpsd__close {
      flex-shrink: 0;
      margin-top: -2px;
    }

    .dpsd__body {
      overflow-y: auto;
      padding: 16px 20px 20px;
      flex: 1;
    }

    .dpsd__section {
      margin-bottom: 20px;
    }

    .dpsd__section:last-child {
      margin-bottom: 0;
    }

    .dpsd__section-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--text-secondary);
      margin: 0 0 10px;
    }

    /* Dietary tag toggle cards */
    .dpsd__diets {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .dpsd__diet {
      --im-button-radius: 10px;
      --im-button-justify: flex-start;
      --im-button-label-gap: 8px;
      --im-button-min-height: 38px;
      --im-button-padding-x: 14px;
      --im-button-font-size: 13px;
      --im-button-text: var(--text-secondary);
      --im-button-text-hover: var(--text);
      --surface-raised: color-mix(in srgb, var(--text) 3%, transparent);
      --surface-hover: color-mix(in srgb, var(--text) 6%, transparent);
      --border-default: var(--border-low);
      --border-strong: var(--border-med);
    }

    .dpsd__diet.selected {
      --surface-raised: color-mix(in srgb, var(--tag-color) 12%, transparent);
      --surface-hover: color-mix(in srgb, var(--tag-color) 18%, transparent);
      --border-default: color-mix(in srgb, var(--tag-color) 40%, transparent);
      --border-strong: color-mix(in srgb, var(--tag-color) 55%, transparent);
      --im-button-text: var(--tag-color);
      --im-button-text-hover: var(--tag-color);
    }

    .dpsd__diet-icon {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .dpsd__diet-name {
      white-space: nowrap;
    }

    /* Allergen flow */
    .dpsd__allergens {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    /* Footer */
    .dpsd__footer {
      padding: 14px 20px;
      border-top: 1px solid var(--border-low);
      display: flex;
      gap: 10px;
      justify-content: flex-end;
    }

    @keyframes dpsd-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes dpsd-slide-up {
      from {
        opacity: 0;
        transform: translate(-50%, -48%) scale(0.96);
      }
      to {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .dpsd__backdrop, .dpsd { animation: none; }
      .dpsd { transform: translate(-50%, -50%); }
    }
  `,
})
export class DietaryPreferencesSetupDialogComponent {
  readonly dietaryTags = DIETARY_TAGS;
  readonly allergens = UK_ALLERGENS;

  protected readonly selectedDietaryTags = signal<string[]>([]);
  protected readonly selectedAllergens = signal<string[]>([]);

  readonly preferencesSaved = output<DietaryPreferencesSaved>();
  readonly cancelled = output<void>();

  // The map these five icons came from now lives on DIETARY_TAGS, so the
  // Settings chips and this dialog cannot drift into two symbols for the
  // same requirement.
  protected readonly iconFor = dietaryIcon;

  protected toggleDietaryTag(value: string): void {
    const current = this.selectedDietaryTags();
    if (current.includes(value)) {
      this.selectedDietaryTags.set(current.filter(t => t !== value));
    } else {
      this.selectedDietaryTags.set([...current, value]);
    }
  }

  protected toggleAllergen(allergen: string): void {
    const current = this.selectedAllergens();
    if (current.includes(allergen)) {
      this.selectedAllergens.set(current.filter(a => a !== allergen));
    } else {
      this.selectedAllergens.set([...current, allergen]);
    }
  }

  protected onSave(): void {
    this.preferencesSaved.emit({
      dietaryTags: this.selectedDietaryTags(),
      allergens: this.selectedAllergens(),
    });
  }

  protected onSkip(): void {
    this.cancelled.emit();
  }

  protected onCancel(): void {
    this.cancelled.emit();
  }
}
